/**
 * Parceiros de dupla (`non_evaluator`) a gravar quando uma realização MAD é EDITADA (MOD-09).
 *
 * A edição só controla a dupla da validação. Os demais parceiros entraram ao longo da apreciação
 * (Nível 2, Pit Stop, Nível 3), na ordem em que entraram, e não pertencem à edição. Gravar só o
 * primeiro da lista, como era feito, tirava esses gestores da realização, que sumia da lista deles
 * e partia a cota da dupla em duas (caso Luciano × Letícia, 23/10/2026).
 */

interface PlayerWithDupla {
  _id?: string;
  mad_dupla?: Array<{ _id?: string } | null>;
}

// Recorte da realização como o aggregate `task` a entrega à edição (com os detalhes dos gestores).
export interface TaskPartnerContext {
  original_manager?: string;
  evaluator?: string[];
  non_evaluator?: string[];
  original_manager_detail?: PlayerWithDupla[] | PlayerWithDupla;
  evaluator_detail?: PlayerWithDupla[];
  non_evaluator_detail?: PlayerWithDupla[];
}

function duplaIds(player: PlayerWithDupla | undefined): string[] {
  const madDupla = player?.mad_dupla;

  if (!Array.isArray(madDupla)) return [];

  return madDupla.map(md => md?._id).filter((id): id is string => !!id).map(String);
}

function detailOf(details: PlayerWithDupla[] | undefined, id: string): PlayerWithDupla | undefined {
  return Array.isArray(details) ? details.find(detail => String(detail?._id) === id) : undefined;
}

// Dupla nos dois sentidos: o espelho da dupla nem sempre está gravado nos dois players.
function areDupla(idA: string, detailA: PlayerWithDupla | undefined, idB: string, detailB: PlayerWithDupla | undefined): boolean {
  return duplaIds(detailA).includes(idB) || duplaIds(detailB).includes(idA);
}

/**
 * Parceiro da validação gravado na realização: o PRIMEIRO do `non_evaluator`, desde que seja de fato
 * dupla do gestor responsável (`original_manager`) e não seja também dupla de algum avaliador da
 * cadeia (aí ele ocupa outro papel e não pertence só à validação). Quando o gestor da validação não
 * tem dupla, o primeiro da lista é o parceiro de um nível acima — e então não há parceiro da validação.
 */
export function validationPartnerOf(task: TaskPartnerContext | undefined): string | undefined {
  if (!task?.original_manager || !Array.isArray(task.non_evaluator) || !task.non_evaluator.length) return undefined;

  const manager = String(task.original_manager);
  const first = String(task.non_evaluator[0]);
  const managerDetail = Array.isArray(task.original_manager_detail) ? task.original_manager_detail[0] : task.original_manager_detail;
  const firstDetail = detailOf(task.non_evaluator_detail, first);

  if (!areDupla(manager, managerDetail, first, firstDetail)) return undefined;

  const evaluatorDetails = task.evaluator_detail;
  const alsoEvaluatorPartner = (task.evaluator || [])
    .map(String)
    .some(evaluatorId => evaluatorId !== manager && areDupla(evaluatorId, detailOf(evaluatorDetails, evaluatorId), first, firstDetail));

  return alsoEvaluatorPartner ? undefined : first;
}

export interface NonEvaluatorEdit {
  // non_evaluator da realização como está gravado.
  savedNonEvaluator: string[] | undefined;
  // `validationPartnerOf` calculado no CARREGAMENTO da edição, com os detalhes dos gestores — a
  // telemetria da tela apaga esses detalhes do estado antes do envio.
  validationPartnerOnLoad: string | undefined;
  // Parceiro escolhido na tela (undefined = opção solo).
  chosenPartnerId: string | undefined;
  // O usuário trocou o responsável pela validação.
  validationDuplaChanged: boolean;
}

/**
 * - Dupla da validação mantida: a lista sai exatamente como está gravada.
 * - Dupla trocada: sai o parceiro da validação — só se ele foi reconhecido no carregamento e continua
 *   na primeira posição — e entra o escolhido na frente (nenhum na opção solo); os demais seguem na
 *   mesma ordem.
 */
export function nonEvaluatorAfterEdit({
  savedNonEvaluator,
  validationPartnerOnLoad,
  chosenPartnerId,
  validationDuplaChanged,
}: NonEvaluatorEdit): string[] {
  const saved = Array.isArray(savedNonEvaluator) ? [...savedNonEvaluator] : [];

  if (!validationDuplaChanged) return saved;

  const outgoingIsFirst = !!validationPartnerOnLoad && saved.length > 0 && String(saved[0]) === validationPartnerOnLoad;
  const kept = outgoingIsFirst ? saved.slice(1) : saved;

  return chosenPartnerId ? [chosenPartnerId, ...kept.filter(id => String(id) !== chosenPartnerId)] : kept;
}
